/* eslint-disable quote-props */
import config from 'config';

export default class RequestAbstract {
    _config = config;
    data;

    handleUnauthorized(errorMsg) {
        this.data.navigate('/auth/login');

        this.data.updateAlertError(errorMsg, 'snackbar', 'error');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }

    updateLocalStorageUser(result) {
        if (result.error === 'OK' && result.user) {
            localStorage.user = JSON.stringify(result.user);
            localStorage.token = result.session;
            localStorage.role = result.user.role.name;
            localStorage.systemRole = result.user.role.systemRole;
        }
        if (result.error === 'OK' && result.session) {
            localStorage.token = result.session;
        }
    }

    async approve(ids) {
        return await this.loadWithAuth(
            this.link + '/approve/' + ids.join(','),
            {
                method: 'GET'
            }
        );
    }

    async changeStatus(data) {
        return await this.loadWithAuth(this.link + '/change-status/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async export(filter, sort, paging) {
        return await this.loadWithAuth(
            this.link + '/export',
            {
                method: 'POST',
                body: JSON.stringify({ filter, sort, paging })
            },
            { fileDownload: true }
        );
    }

    async exportStreaming(filter, sort, paging) {
        return await this.loadWithAuth(
            this.link + '/export/streaming',
            {
                method: 'POST',
                body: JSON.stringify({ filter, sort, paging })
            },
            { fileDownload: true, streaming: true }
        );
    }

    async postForm(link, data, headers = {}) {
        const body = Object.entries(data)
            .map(
                ([key, value]) =>
                    encodeURIComponent(key) + '=' + encodeURIComponent(value)
            )
            .join('&');

        const { language } = this._config;

        return await this.load(link, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/x-www-form-urlencoded',
                Language: localStorage.language || language
            },
            body
        });
    }

    async authPostForm(link, data, headers = {}) {
        const body = Object.entries(data)
            .map(
                ([key, value]) =>
                    encodeURIComponent(key) + '=' + encodeURIComponent(value)
            )
            .join('&');

        const { language } = this._config;

        return await this.loadWithAuth(link, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/x-www-form-urlencoded',
                Language: localStorage.language || language
            },
            body
        });
    }

    async getAll() {
        return await this.getAllFiltered();
    }

    async search(limit, filter = [], sort = [], props) {
        return await this.getFiltered(
            this.link,
            filter,
            sort,
            {
                page: 0,
                pageSize: limit || 25
            },
            props
        );
    }

    async getAllFiltered(
        filter = [],
        sort = [{ field: 'id', direction: 'DESC' }],
        props
    ) {
        return await this.getFiltered(
            this.link,
            filter,
            sort,
            {
                page: 0,
                pageSize: 25
            },
            props
        );
    }

    async getFiltered(link, filter, sort, paging, props = {}) {
        return await this.post(link, { filter, sort, paging, ...props });
    }

    async add(info) {
        return await this.loadWithAuth(this.link + '/add/', {
            method: 'POST',
            body: JSON.stringify(info)
        });
    }
    async addX(data) {
        return await this.loadWithAuth(this.link + '/addX/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async addXPdf(data) {
        return await this.loadWithAuth(
            this.link + '/addX-pdf',
            {
                method: 'POST',
                body: JSON.stringify(data)
            },
            { fileDownload: true }
        );
    }

    async getPdf(ids) {
        return await this.loadWithAuth(
            this.link + '/get-pdf/' + ids,
            {
                method: 'GET'
            },
            { fileDownload: true }
        );
    }

    async update(id, info) {
        return await this.loadWithAuth(this.link + '/update/' + id, {
            method: 'POST',
            body: JSON.stringify(info)
        });
    }

    async delete(ids) {
        return await this.loadWithAuth(this.link + '/delete/' + ids.join(','), {
            method: 'GET'
        });
    }

    async get(link) {
        return await this.loadWithAuth(link, { method: 'GET' });
    }

    async post(link, props) {
        return await this.loadWithAuth(link, {
            method: 'POST',
            body: JSON.stringify(props)
        });
    }

    async loadWithAuth(link, props = {}, params = {}) {
        if (!props.headers) props.headers = {};

        const { language } = this._config;

        props.headers['X-Auth-Token'] = localStorage.token;
        props.headers['Language'] = localStorage.language || language;
        props.headers['Accept-Language'] = localStorage.language || language;

        if (!props.headers['Content-Type']) {
            props.headers['Content-Type'] = 'application/json';
        }

        /**
         * Workaround because of issue of defining Content-Type for File type
         * https://muffinman.io/blog/uploading-files-using-fetch-multipart-form-data/
         * */
        if (params.fileUpload) delete props.headers['Content-Type'];

        return await this.load(
            link,
            props,
            params.fileDownload,
            params.streaming
        );
    }

    async handleStreamingResponse(response, contentDisposition) {
        const reader = response.body.getReader();
        const chunks = [];
        let filename = '';

        if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+?)"/);
            if (match && match[1]) {
                filename = match[1];
            }
        }

        // Read all chunks from the streaming response
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Store each chunk as Uint8Array
            chunks.push(value);
        }

        // Combine all chunks into a single blob
        const blob = new Blob(chunks, { type: 'text/csv' });
        return { blob, filename, error: 'OK' };
    }

    async load(link, props, fileDownload = false, streaming = false) {
        let resp = {};
        const url = this.getFullLink(link);

        let status = 0;
        try {
            const response = await fetch(url, props);

            const headers = response.headers;
            const contentDisposition = headers.get('content-disposition');
            status = response.status;

            if (streaming && response.ok) {
                return await this.handleStreamingResponse(
                    response,
                    contentDisposition
                );
            }

            if (fileDownload) {
                let filename = '';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+?)"/);
                    if (match && match[1]) {
                        filename = match[1];
                    }
                }
                if (response.ok) {
                    resp = await response.blob();
                    return { blob: resp, filename, error: 'OK' };
                }
            }
            resp = await response.json();
        } catch (error) {
            resp = { code: 'error', status, error: 'error' };
        }

        if (status === 401) {
            this.handleUnauthorized(resp.error);
        }

        return resp;
    }

    getFullLink(link) {
        return '/' + link;
    }

    getSortField(sortKey, direction) {
        const sort = [];
        if (sortKey) {
            sort.push({
                field: sortKey,
                direction
            });
        }
        return sort;
    }
}
